import { getCurrentSession, isSupabaseConfigured } from "@/lib/supabase";
import type { CommunicationListParams } from "@/types/communications";
import type {
  BookAppointmentInput,
  CreateContactInput,
  CreateOpportunityInput,
  GoHighLevelCalendar,
  GoHighLevelContact,
  GoHighLevelConversation,
  GoHighLevelMessage,
  GoHighLevelOpportunity,
} from "@/types/gohighlevel";

const SUPABASE_PROJECT_URL = "https://tsnbktwryvnbmgsvzxdt.supabase.co";

function getDataBaseUrl(): string {
  if (import.meta.env.DEV) return "/api/gohighlevel";
  return `${SUPABASE_PROJECT_URL}/functions/v1/gohighlevel-proxy`;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const session = await getCurrentSession();
  if (!session?.access_token) throw new Error("Please log in to access GoHighLevel");
  return { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" };
}

async function parseError(response: Response): Promise<never> {
  let message = response.statusText || "Request failed";
  try {
    const body = await response.json();
    if (body?.error && typeof body.error === "string") message = body.error;
  } catch {
    /* ignore */
  }
  throw new Error(message);
}

class GoHighLevelService {
  async listConversations(
    params: CommunicationListParams = {}
  ): Promise<{ conversations: GoHighLevelConversation[]; nextPageToken?: string }> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const qs = new URLSearchParams({ limit: String(params.limit ?? 20) });
    if (params.pageToken) qs.set("pageToken", params.pageToken);
    if (params.query) qs.set("query", params.query);
    const response = await fetch(`${getDataBaseUrl()}/conversations?${qs}`, {
      headers: await getAuthHeaders(),
    });
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async getConversation(conversationId: string): Promise<GoHighLevelConversation> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const response = await fetch(
      `${getDataBaseUrl()}/conversations/${encodeURIComponent(conversationId)}`,
      { headers: await getAuthHeaders() }
    );
    if (!response.ok) await parseError(response);
    const data = await response.json();
    return data.conversation;
  }

  async listMessages(
    conversationId: string,
    params: CommunicationListParams = {}
  ): Promise<{ messages: GoHighLevelMessage[]; nextPageToken?: string }> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const qs = new URLSearchParams({ limit: String(params.limit ?? 30) });
    if (params.pageToken) qs.set("pageToken", params.pageToken);
    const response = await fetch(
      `${getDataBaseUrl()}/conversations/${encodeURIComponent(conversationId)}/messages?${qs}`,
      { headers: await getAuthHeaders() }
    );
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async sendMessage(conversationId: string, body: string, type = "SMS"): Promise<unknown> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const response = await fetch(
      `${getDataBaseUrl()}/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: "POST",
        headers: await getAuthHeaders(),
        body: JSON.stringify({ body, type }),
      }
    );
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async listContacts(
    params: CommunicationListParams = {}
  ): Promise<{ contacts: GoHighLevelContact[]; nextPageToken?: string }> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const qs = new URLSearchParams({ limit: String(params.limit ?? 20) });
    if (params.pageToken) qs.set("pageToken", params.pageToken);
    if (params.query) qs.set("query", params.query);
    const response = await fetch(`${getDataBaseUrl()}/contacts?${qs}`, {
      headers: await getAuthHeaders(),
    });
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async getContact(contactId: string): Promise<GoHighLevelContact> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const response = await fetch(`${getDataBaseUrl()}/contacts/${encodeURIComponent(contactId)}`, {
      headers: await getAuthHeaders(),
    });
    if (!response.ok) await parseError(response);
    const data = await response.json();
    return data.contact;
  }

  async createContact(input: CreateContactInput): Promise<GoHighLevelContact> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const response = await fetch(`${getDataBaseUrl()}/contacts`, {
      method: "POST",
      headers: await getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) await parseError(response);
    const data = await response.json();
    return data.contact;
  }

  async updateContact(contactId: string, patch: Partial<CreateContactInput>): Promise<GoHighLevelContact> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const response = await fetch(`${getDataBaseUrl()}/contacts/${encodeURIComponent(contactId)}`, {
      method: "PATCH",
      headers: await getAuthHeaders(),
      body: JSON.stringify(patch),
    });
    if (!response.ok) await parseError(response);
    const data = await response.json();
    return data.contact;
  }

  async addContactTag(contactId: string, tag: string): Promise<void> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const response = await fetch(`${getDataBaseUrl()}/contacts/${encodeURIComponent(contactId)}/tags`, {
      method: "POST",
      headers: await getAuthHeaders(),
      body: JSON.stringify({ tag }),
    });
    if (!response.ok) await parseError(response);
  }

  async listOpportunities(
    params: CommunicationListParams = {}
  ): Promise<{ opportunities: GoHighLevelOpportunity[]; nextPageToken?: string }> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const qs = new URLSearchParams({ limit: String(params.limit ?? 20) });
    if (params.pageToken) qs.set("pageToken", params.pageToken);
    if (params.query) qs.set("query", params.query);
    const response = await fetch(`${getDataBaseUrl()}/opportunities?${qs}`, {
      headers: await getAuthHeaders(),
    });
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async createOpportunity(input: CreateOpportunityInput): Promise<GoHighLevelOpportunity> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const response = await fetch(`${getDataBaseUrl()}/opportunities`, {
      method: "POST",
      headers: await getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) await parseError(response);
    const data = await response.json();
    return data.opportunity;
  }

  async listCalendars(): Promise<{ calendars: GoHighLevelCalendar[] }> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const response = await fetch(`${getDataBaseUrl()}/calendars`, {
      headers: await getAuthHeaders(),
    });
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async getCalendarSlots(
    calendarId: string,
    startDate: string,
    endDate: string
  ): Promise<Record<string, unknown>> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const qs = new URLSearchParams({ startDate, endDate });
    const response = await fetch(
      `${getDataBaseUrl()}/calendars/${encodeURIComponent(calendarId)}/slots?${qs}`,
      { headers: await getAuthHeaders() }
    );
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async bookAppointment(calendarId: string, input: BookAppointmentInput): Promise<unknown> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const response = await fetch(
      `${getDataBaseUrl()}/calendars/${encodeURIComponent(calendarId)}/appointments`,
      {
        method: "POST",
        headers: await getAuthHeaders(),
        body: JSON.stringify(input),
      }
    );
    if (!response.ok) await parseError(response);
    return response.json();
  }
}

export const goHighLevelService = new GoHighLevelService();

export function goHighLevelConversationsQueryKey(
  userId: string | undefined,
  params: CommunicationListParams
) {
  return ["gohighlevel-conversations", userId, params] as const;
}

export function goHighLevelMessagesQueryKey(
  conversationId: string | undefined,
  params: CommunicationListParams
) {
  return ["gohighlevel-messages", conversationId, params] as const;
}

export function goHighLevelContactsQueryKey(userId: string | undefined, params: CommunicationListParams) {
  return ["gohighlevel-contacts", userId, params] as const;
}

export function goHighLevelOpportunitiesQueryKey(
  userId: string | undefined,
  params: CommunicationListParams
) {
  return ["gohighlevel-opportunities", userId, params] as const;
}

export function goHighLevelCalendarsQueryKey(userId: string | undefined) {
  return ["gohighlevel-calendars", userId] as const;
}

export function goHighLevelContactQueryKey(contactId: string | undefined) {
  return ["gohighlevel-contact", contactId] as const;
}

export function goHighLevelConversationQueryKey(conversationId: string | undefined) {
  return ["gohighlevel-conversation", conversationId] as const;
}
